/**
 * FAQ Search functionality
 * Filters FAQ items when the search button is clicked or Enter key is pressed
 */
class FaqSearch {
  constructor() {
    this.searchInput = document.querySelector('.faq-hero__search-input');
    this.faqSections = document.querySelectorAll('.faq-section');
    this.faqItems = document.querySelectorAll('.faq-item');
    this.filtersContainer = document.querySelector('.faq-filters');
    this.noResultsMessage = null;
    this.debounceTimer = null;
    this.debounceDelay = 300; // ms
    this.originalContent = {}; // To store original content

    this.init();
  }

  init() {
    if (!this.searchInput || this.faqItems.length === 0) return;

    // Save original content of questions and answers
    this.saveOriginalContent();

    // Create no results message
    this.createNoResultsMessage();

    // Add event for search button
    const searchButton = document.querySelector('.faq-hero__search-button');
    if (searchButton) {
      searchButton.addEventListener('click', () => {
        this.performSearch(this.searchInput.value);
      });
    }

    // Prevent form submission and handle Enter key
    const searchForm = this.searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.performSearch(this.searchInput.value);
      });
    }
    
    // Add event for Enter key in search field
    this.searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.performSearch(this.searchInput.value);
      }
    });

    // Clear search field on page load
    this.searchInput.value = '';

    // Handle filter interaction - reapply search when filter changes
    if (this.filtersContainer) {
      const filterButtons = this.filtersContainer.querySelectorAll('[data-filter-id]');
      filterButtons.forEach(button => {
        button.addEventListener('click', () => {
          // If there's an active search, reapply it with the new filter
          setTimeout(() => {
            if (this.searchInput.value.trim() !== '') {
              this.performSearch(this.searchInput.value);
            }
          }, 100); // Small delay to ensure filter is applied first
        });
      });
    }
  }

  saveOriginalContent() {
    // Save original content of each question and answer
    this.faqItems.forEach((item, index) => {
      const questionEl = item.querySelector('.faq-item__question-text');
      const answerEl = item.querySelector('.faq-item__answer-content');
      
      if (questionEl && answerEl) {
        this.originalContent[index] = {
          question: questionEl.innerHTML,
          answer: answerEl.innerHTML
        };
      }
    });
  }

  createNoResultsMessage() {
    this.noResultsMessage = document.createElement('div');
    this.noResultsMessage.className = 'faq-section__no-results';
    this.noResultsMessage.innerHTML = '<p>No results found.</p>';
    this.noResultsMessage.style.display = 'none';
    
    // Insert after the last FAQ section
    if (this.faqSections.length > 0) {
      const lastFaqSection = this.faqSections[this.faqSections.length - 1];
      lastFaqSection.parentNode.insertBefore(this.noResultsMessage, lastFaqSection.nextSibling);
    }
  }

  // We no longer need this function because search is only activated with the button

  performSearch(searchTerm) {
    searchTerm = searchTerm.toLowerCase();
    let hasVisibleItems = false;
    
    // If search is empty, show all items and apply current filter
    if (searchTerm === '') {
      this.resetSearch();
      return;
    }

    // Add class to indicate active search
    document.body.classList.add('faq-search-active');

    // Get current active filter
    const activeFilterButton = document.querySelector('.faq-filters__tag--active');
    const currentFilterId = activeFilterButton ? activeFilterButton.dataset.filterId : 'all';

    // Loop through all FAQ items and check for matches
    this.faqItems.forEach((item, index) => {
      const questionEl = item.querySelector('.faq-item__question-text');
      const answerEl = item.querySelector('.faq-item__answer-content');
      
      // Restore original content before searching
      if (this.originalContent[index]) {
        questionEl.innerHTML = this.originalContent[index].question;
        answerEl.innerHTML = this.originalContent[index].answer;
      }
      
      const question = questionEl.textContent.toLowerCase();
      const answer = answerEl.textContent.toLowerCase();
      
      // Check if search term is found in question or answer
      const isMatch = question.includes(searchTerm) || answer.includes(searchTerm);
      
      // Check if item matches current category filter
      const itemSection = item.closest('.faq-section');
      const itemCategoryId = itemSection ? itemSection.dataset.faqCategory : '';
      const matchesCategory = currentFilterId === 'all' || itemCategoryId === currentFilterId;
      
      // Show item only if it matches both search term and category filter
      const shouldShow = isMatch && matchesCategory;
      item.style.display = shouldShow ? '' : 'none';
      
      // Highlight matching text if there's a match
      if (shouldShow) {
        hasVisibleItems = true;
        
        // Expand element to show the answer
        const answerContainer = item.querySelector('.faq-item__answer');
        if (answerContainer && !answerContainer.classList.contains('faq-item__answer--active')) {
          // Only expand if match is in the answer and not in the question
          if (answer.includes(searchTerm) && !question.includes(searchTerm)) {
            item.classList.add('faq-item--active');
            item.setAttribute('aria-expanded', 'true');
            
            const questionButton = item.querySelector('.faq-item__question');
            if (questionButton) {
              questionButton.classList.add('faq-item__question--active');
              questionButton.setAttribute('aria-expanded', 'true');
            }
            
            answerContainer.classList.add('faq-item__answer--active');
            answerContainer.setAttribute('aria-hidden', 'false');
          }
        }
        
        // Highlight matching text
        this.highlightText(questionEl, searchTerm);
        this.highlightText(answerEl, searchTerm);
      }
    });

    // Update section visibility based on combined filtering
    this.updateSectionVisibility();
    
    // Show/hide no results message
    this.noResultsMessage.style.display = hasVisibleItems ? 'none' : 'block';
  }

  highlightText(element, searchTerm) {
    if (!element) return;
    
    // Create a regular expression to search for the term (case insensitive)
    const regex = new RegExp(`(${this.escapeRegExp(searchTerm)})`, 'gi');
    
    // Replace text with highlighted version
    // Make sure not to affect existing HTML tags
    const content = element.innerHTML;
    const textNodes = this.getTextNodesIn(element);
    
    textNodes.forEach(node => {
      const text = node.nodeValue;
      const highlighted = text.replace(regex, '<span class="faq-item__highlight">$1</span>');
      
      if (text !== highlighted) {
        const newNode = document.createElement('span');
        newNode.innerHTML = highlighted;
        node.parentNode.replaceChild(newNode, node);
      }
    });
  }

  getTextNodesIn(element) {
    const textNodes = [];
    const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    let node;
    
    while (node = walk.nextNode()) {
      textNodes.push(node);
    }
    
    return textNodes;
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateSectionVisibility() {
    this.faqSections.forEach(section => {
      const sectionItems = section.querySelectorAll('.faq-item');
      let hasVisibleItems = false;
      
      sectionItems.forEach(item => {
        if (item.style.display !== 'none') {
          hasVisibleItems = true;
        }
      });
      
      // Show/hide the entire section based on whether it has visible items
      section.style.display = hasVisibleItems ? '' : 'none';
      
      // If the section is visible, make sure its title is also visible
      const sectionTitle = section.querySelector('.faq-section__title');
      if (sectionTitle) {
        sectionTitle.style.display = hasVisibleItems ? '' : 'none';
      }
    });
  }

  resetSearch() {
    // Remove active search class
    document.body.classList.remove('faq-search-active');

    // Restore original content
    Object.keys(this.originalContent).forEach(index => {
      const item = this.faqItems[index];
      if (item) {
        const questionEl = item.querySelector('.faq-item__question-text');
        const answerEl = item.querySelector('.faq-item__answer-content');
        
        if (questionEl && this.originalContent[index].question) {
          questionEl.innerHTML = this.originalContent[index].question;
        }
        
        if (answerEl && this.originalContent[index].answer) {
          answerEl.innerHTML = this.originalContent[index].answer;
        }
      }
    });

    // Get current active filter and apply it
    const activeFilterButton = document.querySelector('.faq-filters__tag--active');
    const currentFilterId = activeFilterButton ? activeFilterButton.dataset.filterId : 'all';

    // Apply current filter to items and sections
    this.faqItems.forEach(item => {
      const itemSection = item.closest('.faq-section');
      const itemCategoryId = itemSection ? itemSection.dataset.faqCategory : '';
      const matchesCategory = currentFilterId === 'all' || itemCategoryId === currentFilterId;
      
      item.style.display = matchesCategory ? '' : 'none';
    });
    
    // Update section visibility based on current filter
    this.faqSections.forEach(section => {
      const sectionCategoryId = section.dataset.faqCategory;
      const shouldShowSection = currentFilterId === 'all' || sectionCategoryId === currentFilterId;
      
      section.style.display = shouldShowSection ? '' : 'none';
      
      const sectionTitle = section.querySelector('.faq-section__title');
      if (sectionTitle) {
        sectionTitle.style.display = shouldShowSection ? '' : 'none';
      }
    });
    
    // Hide no results message
    if (this.noResultsMessage) {
      this.noResultsMessage.style.display = 'none';
    }
  }
}

// Initialize FAQ search when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  new FaqSearch();
});
